import type { AxiosInstance } from "axios";
import type { QuoteService } from "../contracts/quote.contract";
import type { RequestConfig } from "../types/common";
import type { QuoteRequest, QuoteResponse } from "../types/moneygram";

export class MoneyGramQuoteService implements QuoteService {
	constructor(private axiosInstance: AxiosInstance) {}

	/**
	 * Get quote for a transaction
	 */
	async getQuote(
		request: QuoteRequest,
		config?: RequestConfig,
	): Promise<QuoteResponse> {
		try {
			// Validate required fields for countries that need subdivisions
			this.validateQuoteRequest(request);

			const response = await this.axiosInstance.post(
				"/transactions/quote",
				request,
				{
					params: {
						operation: config?.params?.operation || "withdraw",
						...config?.params,
					},
					...config,
				},
			);

			return this.validateAndTransformQuoteResponse(response.data);
		} catch (error) {
			// Handle quote API error
			throw this.handleApiError(error, "Failed to get transaction quote");
		}
	}

	/**
	 * Validate quote request requirements
	 */
	private validateQuoteRequest(request: QuoteRequest): void {
		const countriesRequiringSubdivision = ["USA", "MEX", "CAN"];

		if (
			countriesRequiringSubdivision.includes(request.destinationCountryCode)
		) {
			if (!request.destinationCountrySubdivisionCode) {
				throw new Error(
					`Region/state selection is required for ${request.destinationCountryCode}`,
				);
			}
		}

		// Validate required fields
		if (!request.destinationCountryCode) {
			throw new Error("Destination country is required");
		}

		if (!request.payoutAmount || request.payoutAmount.value <= 0) {
			throw new Error("Valid payout amount is required");
		}

		if (!request.payoutCurrencyCode) {
			throw new Error("Payout currency is required");
		}

		if (!request.sendCurrencyCode) {
			throw new Error("Send currency is required");
		}
	}

	/**
	 * Validate and transform quote API response to QuoteResponse format
	 */
	private validateAndTransformQuoteResponse(apiData: unknown): QuoteResponse {
		const responseData = apiData as Record<string, unknown>;
		if (!responseData || typeof responseData !== "object") {
			throw new Error("Invalid quote response format");
		}

		// Validate required fields exist
		if (!Array.isArray(responseData.transactions)) {
			throw new Error("No transactions found in quote response");
		}

		// Allow empty transactions if there are errors in the response
		// This handles cases like "Amount exceeded the balance" where we still get valid response structure
		if (responseData.transactions.length === 0 && !responseData.errors) {
			throw new Error("Empty transactions array in quote response");
		}

		// Return the response as QuoteResponse type
		// The API should already return data in the correct format
		// Cast through unknown to satisfy TypeScript's type checking
		return responseData as unknown as QuoteResponse;
	}

	/**
	 * Handle and transform API errors
	 */
	private handleApiError(error: unknown, defaultMessage: string): Error {
		if (error instanceof Error) {
			return error;
		}

		if (typeof error === "object" && error && "response" in error) {
			const axiosError = error as {
				response: { data?: { message?: string }; status?: number };
			};
			const message = axiosError.response?.data?.message || defaultMessage;
			const status = axiosError.response?.status;

			return new Error(`${message}${status ? ` (${status})` : ""}`);
		}

		return new Error(defaultMessage);
	}
}
